import { LabelWithMessage } from '@common/LabelWithMessage';
import { createTableDefaultColumn } from '@common/tableColumn.services';
import { IProjectDesignModification } from '@designModification/designModification.models';
import { useFetchDesignModificationOptionsForLoadingRemovalCert } from '@designModification/designModification.queries';
import { IProjectDrawingRegisterBase } from '@drawing/projectDrawingRegister.models';
import { Accordion, FormFieldMethods, IOption, ResponsivePaginatedTable } from '@gcl/react-core';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import { EPermitToLoadStatus } from '@permitToLoadRemove/permitToLoadRemove.models';
import { ECreateT4FormKeys } from './permitToLoadRemoveDetailPage.constants';
import { IProjectLoadingRemovalCert } from './permitToLoadRemoveDetailPage.models';

interface IPermitToLoadRemoveDetailT3Props {
  formFieldMethods: FormFieldMethods;
  loadingRemovalCertDetail?: IProjectLoadingRemovalCert;
}
export const PermitToLoadRemoveDetailT3: React.FunctionComponent<IPermitToLoadRemoveDetailT3Props> = ({
  formFieldMethods,
  loadingRemovalCertDetail,
}) => {
  const { navigate } = useNavigate();

  const selectedDrawings: IOption<IProjectDrawingRegisterBase>[] = formFieldMethods.watch(
    ECreateT4FormKeys.RELEASED_FOR_CONSTRUCTION_DRAWINGS,
  );

  const selectedDrawingsId = React.useMemo(() => {
    return (selectedDrawings ?? []).map((item) => item.key).filter((id): id is number => !!id);
  }, [JSON.stringify(selectedDrawings)]);

  const { data: designModificationsFromApi } = useFetchDesignModificationOptionsForLoadingRemovalCert(
    {
      ids: selectedDrawingsId,
    },
    loadingRemovalCertDetail?.status == null ||
      loadingRemovalCertDetail?.status === EPermitToLoadStatus.INITIAL ||
      loadingRemovalCertDetail?.status === EPermitToLoadStatus.WAITING_FOR_TWC,
  );

  const designModifications = React.useMemo(() => {
    if (
      loadingRemovalCertDetail?.status == null ||
      loadingRemovalCertDetail?.status === EPermitToLoadStatus.INITIAL ||
      loadingRemovalCertDetail?.status === EPermitToLoadStatus.WAITING_FOR_TWC
    ) {
      return designModificationsFromApi;
    }
    return loadingRemovalCertDetail?.project_LoadingRemovalCertT3?.map((t3) => t3.designModification);
  }, [loadingRemovalCertDetail?.status, JSON.stringify(designModificationsFromApi)]);

  const columns = React.useMemo(() => {
    return [
      createTableDefaultColumn<IProjectDesignModification>(
        'designChangeNo',
        'Design Change No.',
        ({ value, item }) => {
          navigate(routes.t3.view.path, { id: item.id.toString() });
        },
        ({ item }) => {
          return 'gcl-fl-md';
        },
        ({ item }) => {
          return { cursor: 'pointer' };
        },
      ),
      {
        Header: 'Location',
        accessor: 'location',
      },
      {
        Header: 'Element Of Works',
        accessor: 'elementOfWorks',
      },
    ];
  }, []);

  return (
    <>
      <LabelWithMessage labelFor={ECreateT4FormKeys.T3_DESIGN_MODIFICATIONS} text="T3 Design Modifications" />
      <Accordion
        allowClickHeaderToExpand
        className="gcl-gems-permit-to-load-remove-detail-t3-accordion"
        bodyClassName="gcl-gems-permit-to-load-remove-detail-t3-accordion-body"
        content={{
          id: 'T3 Design Modifications',
          onRenderBody: () => {
            return (
              <ResponsivePaginatedTable
                disablePagination
                allowSelectRow={false}
                columns={columns}
                items={designModifications ?? []}
              />
            );
          },
          defaultIsExpanded: true,
        }}
      />
    </>
  );
};
